import { useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import type { PermissionCode } from '@odr/shared';

import { AppShell } from './components/AppShell';
import { Spinner } from './components/ui';
import { onUnauthorized } from './lib/api';
import { homeRouteFor, useAuth } from './lib/auth';

import { LoginPage } from './features/auth/LoginPage';
import { ChangePasswordPage } from './features/auth/ChangePasswordPage';
import { OperatorHome } from './features/dashboard/OperatorHome';
import { SupervisorDashboard } from './features/dashboard/SupervisorDashboard';
import { CoachDashboard } from './features/dashboard/CoachDashboard';
import { MaintenanceDashboard } from './features/dashboard/MaintenanceDashboard';
import { ScanPage } from './features/machines/ScanPage';
import { MachineListPage } from './features/machines/MachineListPage';
import { CiltAnalyticsPage } from './features/analytics/CiltAnalyticsPage';
import { LearningPage } from './features/analytics/LearningPage';
import { ManagementOverviewPage } from './features/analytics/ManagementOverviewPage';
import { MachineHistoryPage } from './features/analytics/MachineHistoryPage';
import { ProblemAnalyticsPage } from './features/analytics/ProblemAnalyticsPage';
import { MachineDetailPage } from './features/machines/MachineDetailPage';
import { QrLandingPage } from './features/machines/QrLandingPage';
import { RunPage } from './features/checklists/RunPage';
import { RunListPage } from './features/checklists/RunListPage';
import { RunReviewPage } from './features/checklists/RunReviewPage';
import { ProblemListPage } from './features/problems/ProblemListPage';
import { ProblemDetailPage } from './features/problems/ProblemDetailPage';
import { OplListPage } from './features/opl/OplListPage';
import { OplDetailPage } from './features/opl/OplDetailPage';
import { TrainingPage } from './features/training/TrainingPage';
import { SkillMatrixPage } from './features/training/SkillMatrixPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { AdminPage } from './features/admin/AdminPage';
import { TemplateEditorPage } from './features/admin/TemplateEditorPage';
import { StandardsPage } from './features/engineering/StandardsPage';
import { NotFoundPage } from './features/NotFoundPage';

function RequireAuth({
  children,
  permission,
  anyPermission,
}: {
  children: React.ReactNode;
  permission?: PermissionCode;
  /** Grants access if the user holds any one of these. */
  anyPermission?: PermissionCode[];
}) {
  const { user, status } = useAuth();

  if (status === 'loading') return <Spinner label="Starting ODR" />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;

  // Force a password change before anything else — accounts are created with a
  // password an administrator knows.
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;

  const denied =
    (permission && user && !user.permissions.includes(permission)) ||
    (anyPermission && user && !anyPermission.some((p) => user.permissions.includes(p)));

  if (denied) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-md rounded-xl border border-steel-200 bg-white p-6 text-center">
          <h1 className="text-lg font-bold text-steel-900">You do not have access to this</h1>
          <p className="mt-2 text-sm text-steel-600">
            Your role does not include this part of ODR. If you think that is wrong, ask your
            supervisor or administrator to review your permissions.
          </p>
        </div>
      </div>
    );
  }

  return <AppShell>{children}</AppShell>;
}

/**
 * What "/" shows depends on who you are.
 *
 * why this is not just <OperatorHome/>: `homeRouteFor` only runs at sign-in, so
 * anyone who reloads the page or types the bare URL — a supervisor, an
 * engineer, a manager — landed on the operator dashboard and got a 403 from an
 * endpoint their role cannot call.
 */
function HomeRoute() {
  const user = useAuth((s) => s.user);

  if (user?.permissions.includes('dashboard.operator')) return <OperatorHome />;

  const target = homeRouteFor(user);
  if (target !== '/') return <Navigate to={target} replace />;

  return (
    <div className="p-6">
      <div className="mx-auto max-w-md rounded-xl border border-steel-200 bg-white p-6 text-center">
        <h1 className="text-lg font-bold text-steel-900">No dashboard assigned</h1>
        <p className="mt-2 text-sm text-steel-600">
          Your role does not include a dashboard yet. Use the menu to reach the areas you do
          have, or ask your administrator to review your permissions.
        </p>
      </div>
    </div>
  );
}

export function App() {
  const restore = useAuth((s) => s.restore);
  const status = useAuth((s) => s.status);
  const navigate = useNavigate();

  useEffect(() => {
    void restore();
  }, [restore]);

  // Central place the API layer tells us the session is unrecoverable.
  useEffect(() => onUnauthorized(() => navigate('/login', { replace: true })), [navigate]);

  if (status === 'loading') {
    return (
      <div className="grid min-h-dvh place-items-center bg-steel-100">
        <Spinner label="Starting ODR" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />

      {/* QR landing: /m/:token — the URL printed on every machine label. */}
      <Route path="/m/:token" element={<QrLandingPage />} />

      <Route path="/" element={<RequireAuth><HomeRoute /></RequireAuth>} />
      <Route path="/scan" element={<RequireAuth><ScanPage /></RequireAuth>} />

      <Route
        path="/machines"
        element={<RequireAuth permission="machine.view"><MachineListPage /></RequireAuth>}
      />
      <Route
        path="/machines/:id"
        element={<RequireAuth permission="machine.view"><MachineDetailPage /></RequireAuth>}
      />
      <Route
        path="/machines/:id/history"
        element={<RequireAuth permission="machine.view"><MachineHistoryPage /></RequireAuth>}
      />

      <Route path="/checklists" element={<RequireAuth><RunListPage /></RequireAuth>} />
      <Route path="/checklists/:runId" element={<RequireAuth><RunPage /></RequireAuth>} />
      <Route path="/checklists/:runId/review" element={<RequireAuth><RunReviewPage /></RequireAuth>} />

      <Route
        path="/problems"
        element={<RequireAuth permission="problem.view"><ProblemListPage /></RequireAuth>}
      />
      <Route
        path="/problems/:id"
        element={<RequireAuth permission="problem.view"><ProblemDetailPage /></RequireAuth>}
      />

      <Route path="/opl" element={<RequireAuth permission="opl.view"><OplListPage /></RequireAuth>} />
      <Route path="/opl/:id" element={<RequireAuth permission="opl.view"><OplDetailPage /></RequireAuth>} />

      <Route path="/training" element={<RequireAuth><TrainingPage /></RequireAuth>} />
      <Route path="/training/:userId" element={<RequireAuth><TrainingPage /></RequireAuth>} />
      <Route
        path="/skill-matrix"
        element={<RequireAuth permission="training.view.all"><SkillMatrixPage /></RequireAuth>}
      />

      <Route
        path="/supervisor"
        element={<RequireAuth permission="dashboard.supervisor"><SupervisorDashboard /></RequireAuth>}
      />
      <Route
        path="/coach"
        element={<RequireAuth permission="dashboard.coach"><CoachDashboard /></RequireAuth>}
      />
      <Route
        path="/maintenance"
        element={<RequireAuth permission="dashboard.maintenance"><MaintenanceDashboard /></RequireAuth>}
      />
      <Route
        path="/management"
        element={<RequireAuth permission="dashboard.management"><ManagementOverviewPage /></RequireAuth>}
      />

      <Route
        path="/reports"
        element={<RequireAuth permission="report.view"><ReportsPage /></RequireAuth>}
      />

      {/* Admin is a set of independently-permissioned areas — an AM Coach who
          can author checklists must be able to reach the editor without being
          granted user management. Each tab checks its own permission. */}
      <Route
        path="/admin"
        element={
          <RequireAuth
            anyPermission={[
              'user.manage',
              'template.view',
              'settings.manage',
              'audit.view',
              'backup.manage',
              'org.view',
            ]}
          >
            <AdminPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/:tab"
        element={
          <RequireAuth
            anyPermission={[
              'user.manage',
              'template.view',
              'settings.manage',
              'audit.view',
              'backup.manage',
              'org.view',
            ]}
          >
            <AdminPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/checklists/:templateId"
        element={<RequireAuth permission="template.view"><TemplateEditorPage /></RequireAuth>}
      />
      <Route
        path="/analytics/cilt"
        element={<RequireAuth permission="report.view"><CiltAnalyticsPage /></RequireAuth>}
      />
      <Route
        path="/analytics/problems"
        element={<RequireAuth permission="report.view"><ProblemAnalyticsPage /></RequireAuth>}
      />
      <Route
        path="/analytics/learning"
        element={<RequireAuth permission="training.view.all"><LearningPage /></RequireAuth>}
      />
      <Route
        path="/engineering-standards"
        element={<RequireAuth permission="standard.view"><StandardsPage /></RequireAuth>}
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
